// Pointer-driven resizing of the chat panel. The widget root is pinned
// to the bottom-right of the viewport, so the user grabs one of the
// other corners and drags to grow/shrink. The integrator picks which
// corner via `window.AevoEchoConfig.resizeCorner`; we default to
// `top-left` because it's the only corner that lets the user scale both
// axes at once when pinned bottom-right.
//
// Size is stored in `localStorage` so the next visit reopens at the
// user's last-chosen dimensions. A single pair of keys is used across
// conversations - resizing is a UI preference, not per-chat state.

import type { ResizeCorner } from './config'
import {
  getCustomAnchorSide,
  getCustomPanelMaxHeightPx,
} from './launcher-position'
import type { FrameRefs } from './frame'

export interface ResizeController {
  dispose: () => void
  /**
   * Re-evaluate which corner of the panel hosts the resize handle.
   * Call this whenever the launcher anchor side may have changed (open,
   * after-drag, viewport resize). No-op on preset CSS-class positions.
   */
  syncHandleSide: () => void
}

const STORAGE_KEY_WIDTH = 'aevo_widget_width'
const STORAGE_KEY_HEIGHT = 'aevo_widget_height'

// Hard min/max so the drag can never produce an unusable size. Min
// leaves enough room for the header + one row of messages + the
// composer; max caps at a fraction of the viewport on either axis.
const MIN_WIDTH_PX = 280
const MIN_HEIGHT_PX = 320
const MAX_WIDTH_VW_PCT = 0.95
const MAX_HEIGHT_VH_PCT = 0.9

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore - storage may be disabled (private mode, disk full, quota) */
  }
}

function clampPx(
  raw: number | null,
  min: number,
  maxViewport: number,
): number | null {
  if (raw === null || !Number.isFinite(raw)) return null
  const upper = Math.max(min, maxViewport)
  return Math.max(min, Math.min(upper, raw))
}

function applyStoredSize(refs: FrameRefs): void {
  const w = Number(safeGetItem(STORAGE_KEY_WIDTH))
  const h = Number(safeGetItem(STORAGE_KEY_HEIGHT))
  const clampedW = clampPx(
    Number.isFinite(w) && w > 0 ? w : null,
    MIN_WIDTH_PX,
    window.innerWidth * MAX_WIDTH_VW_PCT,
  )
  const customMax = getCustomPanelMaxHeightPx(refs)
  const clampedH = clampPx(
    Number.isFinite(h) && h > 0 ? h : null,
    MIN_HEIGHT_PX,
    customMax ?? window.innerHeight * MAX_HEIGHT_VH_PCT,
  )
  if (clampedW !== null) refs.panel.style.width = `${clampedW}px`
  if (clampedH !== null) refs.panel.style.height = `${clampedH}px`
}

/**
 * Attach pointer-based resizing to the chat panel. Returns a disposer
 * the caller can invoke to remove listeners if the widget is ever
 * destroyed (`AevoEcho.destroy()`) or its config changes.
 *
 * `corner: 'none'` renders no handle and is a no-op - useful for
 * integrators who want the default size on every site and don't want
 * users fiddling.
 */
export function attachResize(
  refs: FrameRefs,
  configuredCorner: ResizeCorner,
): ResizeController {
  if (configuredCorner === 'none') {
    return { dispose: () => {}, syncHandleSide: () => {} }
  }

  applyStoredSize(refs)

  const handle = document.createElement('span')
  handle.setAttribute('role', 'separator')
  handle.setAttribute('aria-orientation', 'horizontal')
  handle.setAttribute('aria-label', 'Resize chat panel')
  // No glyph - the visual affordance is a subtle corner-vignette
  // painted by CSS. The element itself is still hit-tested for
  // pointer events; the visual weight is kept low so it never
  // competes with the header or message content.
  refs.panel.appendChild(handle)

  // Stateful: which logical corner the handle currently occupies. The
  // setter rewrites the className so the CSS picks up the right corner
  // vignette + cursor, and the math in `onMove` uses the same variable.
  let effectiveCorner: ResizeCorner = configuredCorner

  const HANDLE_BASE = 'se-resize-handle'

  const setHandleCorner = (next: ResizeCorner): void => {
    if (next === 'none') return
    effectiveCorner = next
    handle.className = `${HANDLE_BASE} is-${next}`
  }
  setHandleCorner(configuredCorner)

  /**
   * If the launcher uses a custom drag anchor, mirror the configured
   * corner to the opposite side when the anchor is on the *left* of the
   * viewport — that way the handle is always on the side the panel
   * grows toward, and the natural "drag away from the anchor" grow
   * direction is preserved.
   *
   *   anchor right (default)  → top-left handle, drag left/up grows
   *   anchor left             → top-right handle, drag right/up grows
   */
  const syncHandleSide = (): void => {
    const side = getCustomAnchorSide(refs.root)
    if (side === null) {
      setHandleCorner(configuredCorner)
      return
    }
    const next = mirrorCornerForAnchor(configuredCorner, side)
    setHandleCorner(next)
  }
  syncHandleSide()

  const rect = () => refs.panel.getBoundingClientRect()

  const maxPanelHeightPx = (): number => {
    const custom = getCustomPanelMaxHeightPx(refs)
    return custom ?? window.innerHeight * MAX_HEIGHT_VH_PCT
  }

  const onPointerDown = (e: PointerEvent): void => {
    e.preventDefault()
    // Re-sync in case the user dragged the launcher to the other side
    // *between* open and grab. Cheap (one classList read + one write).
    syncHandleSide()
    handle.setPointerCapture(e.pointerId)
    refs.panel.classList.add('is-resizing')

    const startRect = rect()
    const startX = e.clientX
    const startY = e.clientY
    const startW = startRect.width
    const startH = startRect.height
    const cornerAtStart = effectiveCorner
    // Cache the viewport-derived caps *once* at pointer-down. Computing
    // them on every move would create a feedback loop on custom
    // bottom-anchored positions (root grows upward while panel grows,
    // shrinking `spaceAbove`, which would snap the height back down).
    const maxW = Math.max(MIN_WIDTH_PX, window.innerWidth * MAX_WIDTH_VW_PCT)
    const maxH = Math.max(MIN_HEIGHT_PX, maxPanelHeightPx())

    const onMove = (ev: PointerEvent): void => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY

      // Direction coefficients: which way does movement *grow* the
      // panel from this corner? The panel is anchored to the corner
      // opposite the handle, so each handle grows AWAY from its own
      // corner:
      //   top-left    -> drag up/left grows both axes (anchor BR)
      //   top-right   -> drag up/right grows both axes (anchor BL)
      //   bottom-left -> drag down/left grows both axes (anchor TR)
      //   bottom-right -> drag down/right grows both axes (anchor TL)
      let newW = startW
      let newH = startH
      if (cornerAtStart === 'top-left') {
        newW = startW - dx
        newH = startH - dy
      } else if (cornerAtStart === 'top-right') {
        newW = startW + dx
        newH = startH - dy
      } else if (cornerAtStart === 'bottom-left') {
        newW = startW - dx
        newH = startH + dy
      } else if (cornerAtStart === 'bottom-right') {
        newW = startW + dx
        newH = startH + dy
      }

      const clampedW = Math.max(MIN_WIDTH_PX, Math.min(maxW, newW))
      const clampedH = Math.max(MIN_HEIGHT_PX, Math.min(maxH, newH))
      refs.panel.style.width = `${clampedW}px`
      refs.panel.style.height = `${clampedH}px`
    }

    const onUp = (): void => {
      handle.releasePointerCapture(e.pointerId)
      refs.panel.classList.remove('is-resizing')
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)

      const finalRect = rect()
      safeSetItem(STORAGE_KEY_WIDTH, String(Math.round(finalRect.width)))
      safeSetItem(STORAGE_KEY_HEIGHT, String(Math.round(finalRect.height)))
    }

    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }

  handle.addEventListener('pointerdown', onPointerDown)

  return {
    dispose: () => {
      handle.removeEventListener('pointerdown', onPointerDown)
      handle.remove()
    },
    syncHandleSide,
  }
}

/**
 * Pick the corner on the opposite horizontal side from the launcher
 * anchor, preserving the vertical edge of the configured corner. This
 * keeps the drag direction natural: handle is always on the side the
 * panel grows toward, not pinned under the launcher.
 */
function mirrorCornerForAnchor(
  corner: ResizeCorner,
  side: 'left' | 'right',
): ResizeCorner {
  if (corner === 'none') return corner
  const wantLeftHandle = side === 'right'
  const [vert, horiz] = corner.split('-') as ['top' | 'bottom', 'left' | 'right']
  const nextHoriz: 'left' | 'right' = wantLeftHandle ? 'left' : 'right'
  if (horiz === nextHoriz) return corner
  return `${vert}-${nextHoriz}` as ResizeCorner
}
